import React, { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { ICheckoutSession, OpenBankingPaymentSummary, OpenBankingPaymentView } from '../state/Radom'
import LinkExternal from '../components/LinkExternal'
import TimeText from '../components/TimeText'
import { PAY_BASE_URL, RADOM_COLORS } from '../util/Constants'
import Table from '../components/Table'
import { StatusBadge, sessionStatusLabel } from '../views/CheckoutSessions'
import { ExternalLinkText } from '../views/Events'
import {
  ItemDataThumbnail,
  ProductThumbnail, PaymentDetailsSection
} from './PaymentDetails'
import RefundDetails from './RefundDetails'
import { Section, SubTitle, SectionContent, SectionItem } from './Shared'
import { OpenBankingPaymentContent } from './OpenBankingPaymentDrawer'
import {
  effectiveSessionStatus,
  isOpenBankingPaymentView,
  openBankingViewFromSession
} from '../util/OpenBankingPayment'

interface CheckoutSessionDetailsProps {
  checkoutSession: ICheckoutSession
  netPayment: number
  /**
   * The session's bank payment, when the page has loaded it. Without it the
   * payment is put together from what the session itself carries.
   */
  bankPayment?: OpenBankingPaymentView | OpenBankingPaymentSummary
}

// The same cards as the payment drawer, flowed into columns on a full page.
const BankPaymentColumns = styled.div`
  column-gap: 14px;
  columns: 2 340px;

  > * {
    break-inside: avoid;
    margin-bottom: 14px;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`

const BankPaymentLink = styled(Link)`
  align-items: center;
  color: var(--ink, #20242d);
  display: inline-flex;
  font-size: 13px;
  gap: 5px;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }
`

const CheckoutSessionDetails = ({
  checkoutSession,
  netPayment,
  bankPayment: loadedBankPayment
}: CheckoutSessionDetailsProps): ReactElement => {
  const bankPayment = loadedBankPayment ?? openBankingViewFromSession(checkoutSession)
  const sessionStatus = effectiveSessionStatus(checkoutSession.sessionStatus, bankPayment?.status)
  const bankPaymentView = bankPayment && isOpenBankingPaymentView(bankPayment) ? bankPayment : undefined
  const paymentEventId: string | undefined = bankPaymentView?.paymentEventId ??
    checkoutSession.payment?.openBanking?.paymentEventId

  return <>
    <Section>
      <SubTitle style={{ fontSize: 18 }}>
        Checkout session details
      </SubTitle>

      <SectionContent>
        <SectionItem>
          <SubTitle>ID</SubTitle>
          <ExternalLinkText href={`${PAY_BASE_URL}/checkout/${checkoutSession.id}`} target='_blank'>
            {checkoutSession.id}
            <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
          </ExternalLinkText>
        </SectionItem>

        <SectionItem>
          <SubTitle>Status</SubTitle>
          <StatusBadge status={sessionStatus}>{sessionStatusLabel(sessionStatus)}</StatusBadge>
        </SectionItem>

        <SectionItem>
          <SubTitle>Created at</SubTitle>
          <div>
            <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(checkoutSession.createdAt)} />
          </div>
        </SectionItem>

        {
          checkoutSession.items && checkoutSession.items.length > 0 &&
          <SectionItem>
            <SubTitle>Line items</SubTitle>
            <div style={{ fontSize: 14 }}>
              {checkoutSession.items.map((item, i) => <ItemDataThumbnail key={i} p={item} />)}
            </div>
          </SectionItem>
        }
        {
          checkoutSession.products && checkoutSession.products.length > 0 &&
          <SectionItem>
            <SubTitle>Products</SubTitle>
            <div style={{ fontSize: 14 }}>
              {checkoutSession.products.map((item, i) =>
                <ProductThumbnail key={i} p={item} />
              )}
            </div>
          </SectionItem>
        }

      </SectionContent>
      {
        checkoutSession && checkoutSession.metadata.length > 0 &&
        <div style={{ fontSize: 14 }}>
          <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Metadata</SubTitle>
          <Table
            disableHoverStyle
            headers={[
              <span key="productType">Key</span>,
              <span key="quantity">Value</span>
            ]}
            items={
              checkoutSession.metadata.map(p => [
                <span key="type">{p.key}</span>,
                <span key="quantity">{p.value}</span>
              ])
            }
            isLoading={false}
          />
        </div>
      }
    </Section>

    {
      // One view of the bank payment, the same one the payment drawer shows:
      // a single status in plain words, what happened, and what was credited.
      bankPayment &&
      <Section>
        <SubTitle style={{ fontSize: 18 }}>
          Open banking payment
        </SubTitle>
        <BankPaymentColumns>
          <OpenBankingPaymentContent payment={bankPayment} hideRefunds hideMetadata />
        </BankPaymentColumns>
        {
          paymentEventId &&
          <BankPaymentLink to={`/transactions?selected=${paymentEventId}&q=${paymentEventId}`}>
            View in transactions
            <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
          </BankPaymentLink>
        }
      </Section>
    }

    {
      checkoutSession.payment?.managed &&
      <PaymentDetailsSection payment={{ managed: checkoutSession.payment?.managed }} netPayment={netPayment} />
    }

    {
      checkoutSession?.payment?.managedTokenDelegatePayment &&
      <PaymentDetailsSection payment={{ managed: checkoutSession.payment.managedTokenDelegatePayment }}
        netPayment={netPayment} />
    }

    {
      checkoutSession.refunds.length > 0 &&
      <RefundDetails refunds={checkoutSession.refunds} />
    }
  </>
}

export default CheckoutSessionDetails
